import {
  Component,
  Input,
  Signal,
  ViewChild,
  computed,
  signal,
} from '@angular/core';
import { AsyncPipe, NgFor, NgForOf, NgIf } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { BehaviorSubject, Observable, combineLatest, forkJoin, of } from 'rxjs';
import { map, shareReplay, startWith, switchMap } from 'rxjs/operators';

import {
  IsLoadingPaginationResult,
  PaginationResult,
  PersonAttributes,
} from '~app/models';
import { UserGroupAttributes } from '~app/models/user-group';
import { UserGroupService } from '~app/services/user-group.service';
import { SimpleStringSortBy } from '~app/utilities/simple-sortby';

import { CiaoFormsModule } from '~components/shared/forms/forms.module';
import { CiaoSharedModule } from '~components/shared/shared.module';
import { PageSelectorComponent } from '~components/report-table/page-selector/page-selector.component';
import { pageData } from '~components/report-table/client-side-pagination.functions';
import { NgForTrackByIdDirective } from '~app/utilities/ng-for-track-by-id.directive';
import { UserGroupModalComponent } from '../../user-group-page/user-group-modal/user-group-modal.component';

type MyTeamsTableRow = {
  team: UserGroupAttributes;
  members: {
    person: PersonAttributes;
    team_member: boolean;
    supervisor: boolean;
    dispatcher: boolean;
    guest: boolean;
    maxRole: 'Supervisor' | 'Team Member' | 'Dispatcher' | 'Guest';
  }[];
  expanded: Signal<boolean>;
};

@Component({
    selector: 'ciao-user-profile-teams-table',
    imports: [
        AsyncPipe,
        NgFor,
        NgForOf,
        NgIf,
        MatIconModule,
        CiaoFormsModule,
        CiaoSharedModule,
        PageSelectorComponent,
        NgForTrackByIdDirective,
        UserGroupModalComponent,
    ],
    templateUrl: './user-profile-teams-table.component.html',
    styleUrls: ['./user-profile-teams-table.component.less', '../shared.less']
})
export class UserProfileTeamsTableComponent {
  constructor(private readonly userGroupService: UserGroupService) {}

  @ViewChild('userGroupModal') userGroupModal: UserGroupModalComponent;

  readonly personId$ = new BehaviorSubject('');
  @Input() set personId(val: string) {
    this.personId$.next(val);
  }
  get personId() {
    return this.personId$.value;
  }
  /**
   * @see [isExpanded]
   * @see [expand]
   */
  selectedTeamId = signal('');

  readonly teamsList$ = this.getTeamsListObservable();

  pagingInfo$ = new BehaviorSubject({ limit: 0, offset: 0 });
  readonly paginationResult$: Observable<PaginationResult<MyTeamsTableRow>> =
    combineLatest([this.teamsList$, this.pagingInfo$]).pipe(
      map(([groups, { limit, offset }]) => pageData(groups, limit, offset)),
      startWith(IsLoadingPaginationResult())
    );

  isExpanded(id) {
    return computed(() => {
      return this.selectedTeamId() === id;
    });
  }
  expand(id) {
    if (this.selectedTeamId() === id) {
      this.selectedTeamId.set('');
    } else {
      this.selectedTeamId.set(id);
    }
  }

  openTeamModal(team: UserGroupAttributes) {
    this.userGroupModal.openModal(team, 'Edit');
  }

  processTeamMembersIntoTeam(
    rawMemberList: {
      team_member: PersonAttributes[];
      supervisor: PersonAttributes[];
      dispatcher: PersonAttributes[];
      guest: PersonAttributes[];
    },
    team: UserGroupAttributes
  ) {
    let membersById: {
      [id: string]: MyTeamsTableRow['members'][number];
    } = {};
    rawMemberList.team_member.forEach((person) => {
      membersById[person.id] = {
        person,
        team_member: true,
        supervisor: false,
        dispatcher: false,
        guest: false,
        maxRole: 'Team Member',
      };
    });
    rawMemberList.dispatcher.forEach((person) => {
      // Dispatchers are non-USDA-only and team_members are USDA-only, so the
      // same person should never have both — but be defensive.
      if (membersById[person.id]) {
        membersById[person.id].dispatcher = true;
      } else {
        membersById[person.id] = {
          person,
          team_member: false,
          supervisor: false,
          dispatcher: true,
          guest: false,
          maxRole: 'Dispatcher',
        };
      }
    });
    rawMemberList.supervisor.forEach((person) => {
      if (membersById[person.id]) {
        membersById[person.id].supervisor = true;
        membersById[person.id].maxRole = 'Supervisor';
      } else {
        membersById[person.id] = {
          person,
          team_member: false,
          supervisor: true,
          dispatcher: false,
          guest: false,
          maxRole: 'Supervisor',
        };
      }
    });
    // Guest is the lowest-priority role: flag existing members but never demote
    // a higher role's maxRole; only members whose sole role is guest show 'Guest'.
    rawMemberList.guest.forEach((person) => {
      if (membersById[person.id]) {
        membersById[person.id].guest = true;
      } else {
        membersById[person.id] = {
          person,
          team_member: false,
          supervisor: false,
          dispatcher: false,
          guest: true,
          maxRole: 'Guest',
        };
      }
    });
    let memberList = Object.keys(membersById)
      .map((key) => membersById[key])
      .sort(SimpleStringSortBy('person.displayName'));

    return {
      team: team,
      members: memberList,
      expanded: this.isExpanded(team.id),
    };
  }

  /**
   * The tier 0 "System" group is an internal catch-all: every non-USDA account
   * is assigned a guest role there at registration, so it holds hundreds of
   * unrelated users. It isn't a team the user belongs to, so keep it out of the
   * profile's team list — filtering here also skips fetching its member list.
   */
  isRealTeam(team: UserGroupAttributes) {
    return team.tier !== 0;
  }

  getTeamsListObservable() {
    return this.personId$.pipe(
      switchMap((personId) =>
        this.userGroupService.searchUserGroups({
          personIds: [this.personId],
          roleIds: ['team_member', 'supervisor', 'dispatcher', 'guest'],
        })
      ),
      this.userGroupService.op_sortGroups,
      map((teams) => teams.filter((team) => this.isRealTeam(team))),
      switchMap((teams) => {
        // forkJoin([]) completes without emitting, which would leave the table
        // stuck on its loading row for a user whose only group was System.
        if (teams.length === 0) {
          return of([] as MyTeamsTableRow[]);
        }

        let memberLists$ = teams.map((team) =>
          this.userGroupService.getMembersByRole(team.id, [
            'team_member',
            'supervisor',
            'dispatcher',
            'guest',
          ])
        );
        let teamList$ = forkJoin(memberLists$).pipe(
          map((memberLists) =>
            memberLists.map((rawMemberList, index) =>
              this.processTeamMembersIntoTeam(rawMemberList, teams[index])
            )
          )
        );

        return teamList$;
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }
}
